<template>
  <ValidationObserver v-slot="{handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal :show-confirmation="showConfirmation" modal-class="mobile-full-screen" :ok-title="$t('Submit')"
                   :title="$t('Create Bid')" :visible="modal.show" :is-loading="loading" :data="model"
                   @hidden="onHidden" @show="onShow" @ok.prevent="handleSubmit(onSubmit)" scrollable size="col-md-12">
      <div class="row">
        <div class="col-sm-6">
          <input-widget @keyup.prevent="onPaddleNumberKeyUp" @keydown="onPaddleNumberKeyDown"
                        @blur="onPaddleNumberBlur" :model="model" type="text" attribute="paddle_number"/>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div class="d-flex justify-content-start" v-if="bidder">
            <customer-email-address-info :customer="bidder.customer"/>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-6" v-if="modal.source !== SOURCE_LOTS_TABLE">
          <lot-select-input-widget :disabled="isLotInputDisabled" :model="model" :auction-uuid="model.auction_uuid"
                                   status="lotted" attribute="lot_uuid"/>
        </div>
        <div class="col-sm-6">
          <currency-input-widget :model="model" :currency="currencySymbol" attribute="amount"/>
        </div>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
import {createNamespacedHelpers} from "vuex";
import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
import BidModel from "./Bid.model"
import BiddersService from "@/modules/bidders/BiddersService";
import CustomerEmailAddressInfo from "@/core/components/customer-info/CustomerEmailAddressInfo.vue";
import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget.vue";
import InputWidget from "@/core/components/input-widget/InputWidget.vue";
import LotSelectInputWidget from "@/core/widgets/LotSelectInputWidget.vue";
import {SOURCE_BIDS_TABLE, SOURCE_LOTS_TABLE} from "@/modules/constants";

const {mapState, mapActions} = createNamespacedHelpers('bids')
const {mapState: mapStateSales} = createNamespacedHelpers('sales');
const {mapState: mapStateGlobal} = createNamespacedHelpers('global');

export default {
  name: "BidsModal",
  components: {
    CustomerEmailAddressInfo,
    LotSelectInputWidget,
    InputWidget,
    CurrencyInputWidget,
    ArtisioModal
  },
  data() {
    return {
      loading: false,
      showConfirmation: true,
      model: new BidModel(),
      bidder: null,
      SOURCE_LOTS_TABLE,
    }
  },
  computed: {
    ...mapState({
      modal: state => state.modal,
    }),
    ...mapStateSales({
      currentSale: state => state.view.currentSale
    }),
    ...mapStateGlobal({
      hasArtisioTimedProvider: state => state.hasArtisioTimedProvider,
    }),
    isLotInputDisabled() {
      return !!this.model.uuid || !this.model.auction_uuid;
    },
    currencySymbol() {
      return this.determineCurrencySymbol();
    },
  },

  methods: {
    ...mapActions(['hideModal', 'createBid']),
    onShow() {
      this.showConfirmation = true;
      this.bidder = null;
      this.setAuctionUUID();
    },
    onHidden() {
      this.model = new BidModel();
      this.hideModal();
      this.errorMessage = null;
    },
    async onSubmit() {
      let response;
      this.loading = true;

      if (this.modal.source === SOURCE_LOTS_TABLE) {
        this.populateModelFromLot(this.modal.object.lot);
      }

      if (!this.model.uuid) {
        response = await this.createBid({...this.model.toJSON()});
      }
      this.loading = false;

      if (response.success) {
        this.showConfirmation = false;
        this.hideModal();
        this.$toast('success', this.$t('Bid(s) successfully created'));
      } else {
        if (response.toastError) {
          this.$errorDialog(response.toastError);
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else {
            this.model.setMultipleErrors(response.body);
          }
        }
      }
    },
    onPaddleNumberKeyUp($event) {
      if ($event.keyCode === 13) {
        this.getBidderInfo();
      }
    },
    onPaddleNumberKeyDown($event) {
      if ($event.keyCode === 13) {
        $event.preventDefault();
      }
    },
    onPaddleNumberBlur() {
      if (!this.bidder || this.bidder.paddle_number !== this.model.paddle_number) {
        this.getBidderInfo();
      }
    },
    async getBidderInfo() {
      this.model.resetErrors('paddle_number')

      const sale_no = this.modal.source === SOURCE_LOTS_TABLE ? this.modal.object.lot.sale_no : this.modal.sale.sale_no
      const { body, success } = await BiddersService.getBidder(sale_no, this.model.paddle_number, 'customer');

      if (success) {
        this.bidder = body;
        this.model.bidder_uuid = this.bidder.uuid
      } else {
        this.model.paddle_number
            ? this.model.setErrors('paddle_number', [this.$i18n.t('No bidder is assigned to this paddle number')])
            : this.model.getFirstError('paddle_number')
        this.bidder = null;
        this.model.bidder_uuid = null;
      }
    },
    populateModelFromLot(lot) {
      const { uuid: lot_uuid, lot_no } = lot;
      this.model.lot_uuid = lot_uuid;
      this.model.lot_no = lot_no;
    },
    determineCurrencySymbol() {
      if (this.modal.source === SOURCE_LOTS_TABLE) {
        return this.modal.object.lot.currency.symbol;
      } else if (this.modal.source === SOURCE_BIDS_TABLE && this.currentSale) {
        return this.currentSale.currency.symbol;
      }
      return null;
    },
    setAuctionUUID() {
      if (this.modal.source === SOURCE_LOTS_TABLE) {
        this.model.auction_uuid = this.modal.object.lot.auction_uuid;
      } else {
        this.model.auction_uuid = this.currentSale.uuid;
      }
    },
  },
}
</script>

<style scoped>

</style>
